import { computed, MaybeRefOrGetter, toValue } from 'vue'
import { FragmentType, graphql, useFragment } from '@/gql'
import { AccountType } from '@/gql/graphql'
import { $t } from '@/composables/translation'
import {
  CUSTOM_FIELD_VALUES_VIEW_FRAGMENT,
  FieldableFormCustomFieldFragment,
} from '@/components/Fieldable/graphql/fragments'
import type { CustomFieldsSection } from '@/components/Hire/HirePageParts/HireCustomFieldsGroup.vue'

export const HireCustomFieldSectionsFragment = graphql(`
  fragment HireCustomFieldSections on Hire {
    id
    worker {
      id
      name
    }
    recruiter {
      id
      name
    }
    customFieldValues {
      ...CustomFieldValuesView
    }
  }
`)

export type HireCustomFieldsAnsweredBy = AccountType.Company | AccountType.Worker

interface UseHireCustomFieldSectionsOptions {
  recruiterManagesWorker: MaybeRefOrGetter<boolean>
  hire: MaybeRefOrGetter<FragmentType<typeof HireCustomFieldSectionsFragment> | null | undefined>
  fields: MaybeRefOrGetter<FragmentType<typeof FieldableFormCustomFieldFragment>[]>
}

// Splits a hire's custom fields by the account that answers them (the company, or the worker side),
// and builds the sections to show. The worker side is the staffing agency when it manages the worker.
export function useHireCustomFieldSections(options: UseHireCustomFieldSectionsOptions) {
  const fields = computed(() => toValue(options.fields))
  const hire = computed(() => useFragment(HireCustomFieldSectionsFragment, toValue(options.hire)))
  const values = computed(() => hire.value?.customFieldValues ?? [])

  // A field asked only of the other party (worker vs managing agency) doesn't apply to this hire at all.
  const answerer = (field: (typeof fields.value)[number]): HireCustomFieldsAnsweredBy | null => {
    const { workerInputAllowed, recruiterInputAllowed } = useFragment(
      FieldableFormCustomFieldFragment,
      field,
    )

    if (!workerInputAllowed && !recruiterInputAllowed) return AccountType.Company

    return (
      toValue(options.recruiterManagesWorker) ? recruiterInputAllowed : workerInputAllowed
    )
      ? AccountType.Worker
      : null
  }

  const workerFields = computed(() =>
    fields.value.filter((field) => answerer(field) === AccountType.Worker),
  )
  const companyFields = computed(() =>
    fields.value.filter((field) => answerer(field) === AccountType.Company),
  )

  const idsOf = (list: (typeof fields.value)[number][]) =>
    new Set(list.map((field) => useFragment(FieldableFormCustomFieldFragment, field).id))
  const workerFieldIds = computed(() => idsOf(workerFields.value))
  const companyFieldIds = computed(() => idsOf(companyFields.value))
  const valueFieldId = (value: (typeof values.value)[number]) =>
    useFragment(CUSTOM_FIELD_VALUES_VIEW_FRAGMENT, value).customField.id

  const workerValues = computed(() =>
    values.value.filter((value) => workerFieldIds.value.has(valueFieldId(value))),
  )
  // Archived fields are no longer asked for, but what was already answered stays readable.
  const archivedValues = computed(() =>
    values.value.filter(
      (value) => useFragment(CUSTOM_FIELD_VALUES_VIEW_FRAGMENT, value).customField.archivedAt !== null,
    ),
  )
  const companyValues = computed(() => [
    ...values.value.filter((value) => companyFieldIds.value.has(valueFieldId(value))),
    ...archivedValues.value,
  ])

  const hasFields = computed(
    () => companyFields.value.length + workerFields.value.length > 0 || archivedValues.value.length > 0,
  )

  // Who answers the worker's fields: the staffing agency when it manages them, else the worker.
  const answererName = computed(() =>
    toValue(options.recruiterManagesWorker)
      ? (hire.value?.recruiter?.name ?? $t('the staffing agency'))
      : (hire.value?.worker?.name?.split(' ')[0] ?? $t('the worker')),
  )

  const companySection = computed<CustomFieldsSection>(() => ({
    key: AccountType.Company,
    label: $t('Answered by you'),
    fields: companyFields.value,
    values: companyValues.value,
  }))
  const workerSection = computed<CustomFieldsSection>(() => ({
    key: AccountType.Worker,
    label: $t('Answered by :name', { name: answererName.value }),
    fields: workerFields.value,
    values: workerValues.value,
  }))

  // The labels only help when both parties answer fields.
  const sections = computed<CustomFieldsSection[]>(() => {
    const both = companyFields.value.length > 0 && workerFields.value.length > 0

    return [companySection.value, workerSection.value].map((section) => ({
      ...section,
      label: both ? section.label : undefined,
    }))
  })

  return {
    companyFields,
    workerFields,
    hasFields,
    companySection,
    workerSection,
    sections,
  }
}
